import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Trophy, Book, Users, Award, Home as HomeIcon, Medal, Calendar, Table, FileText, GraduationCap, LogIn, Edit } from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';
import AuthModal from './components/Auth/AuthModal';
import UserMenu from './components/Auth/UserMenu';
import ClassCalendar from './components/ClassBooking/ClassCalendar';
import SimpleCalendarBooking from './components/ClassBooking/SimpleCalendarBooking';
import WeeklyCalendar from './components/ClassBooking/WeeklyCalendar';
import AdminCalendar from './components/ClassBooking/AdminCalendar';
import InstructorDashboard from './components/ClassBooking/InstructorDashboard';
import AdminPanel from './components/ClassBooking/AdminPanel';
import MyBookings from './components/ClassBooking/MyBookings';
import { testSupabaseConnection, testAdminFunctions } from './utils/supabaseTest';
import { verifyAdminAccount, testAdminFunctionality } from './utils/adminVerification';

function App() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    // Verificar sesión actual
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Escuchar cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    // Test Supabase connection in development
    if (import.meta.env.DEV) {
      testSupabaseConnection().then(() => {
        console.log('Supabase connection test completed');
      });
    }

    return () => subscription.unsubscribe();
  }, []);

  // Test admin functions when user changes
  useEffect(() => {
    if (user && import.meta.env.DEV) {
      setTimeout(() => {
        testAdminFunctions().then(() => {
          // Verificar específicamente la cuenta de admin
          if (user.email === 'padelparksanluis@gmail.com') {
            verifyAdminAccount().then(result => {
              console.log('🔐 Verificación de cuenta admin:', result);
              if (result.success) {
                testAdminFunctionality().then(funcResult => {
                  console.log('🧪 Pruebas de funcionalidad admin:', funcResult);
                });
              }
            });
          }
        });
      }, 1000);
    }
  }, [user]);

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
  };

  const handleLogout = () => {
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between h-16">
              <div className="flex">
                <div className="flex-shrink-0 flex items-center">
                  <Trophy className="h-8 w-8 text-blue-600" />
                  <span className="ml-2 text-xl font-bold text-gray-900">Padel Park</span>
                </div>
                <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                  <Link
                    to="/"
                    className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-900 border-b-2 border-transparent hover:border-blue-500"
                  >
                    <HomeIcon className="h-5 w-5 mr-1" />
                    Inicio
                  </Link>
                  <Link
                    to="/torneo"
                    className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 border-b-2 border-transparent hover:border-blue-500 hover:text-gray-900"
                  >
                    <Trophy className="h-5 w-5 mr-1" />
                    4to Torneo Anual
                  </Link>
                  <Link
                    to="/reglamento"
                    className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 border-b-2 border-transparent hover:border-blue-500 hover:text-gray-900"
                  >
                    <Book className="h-5 w-5 mr-1" />
                    Reglamento
                  </Link>
                  <Link
                    to="/buscar-pareja"
                    className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 border-b-2 border-transparent hover:border-blue-500 hover:text-gray-900"
                  >
                    <Users className="h-5 w-5 mr-1" />
                    Busco Pareja
                  </Link>
                  <Link
                    to="/liga"
                    className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 border-b-2 border-transparent hover:border-blue-500 hover:text-gray-900"
                  >
                    <Medal className="h-5 w-5 mr-1" />
                    Liga Padel Park
                  </Link>
                  <Link
                    to="/clases"
                    className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 border-b-2 border-transparent hover:border-blue-500 hover:text-gray-900"
                  >
                    <GraduationCap className="h-5 w-5 mr-1" />
                    Clases
                  </Link>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                {user ? (
                  <UserMenu user={user} onLogout={handleLogout} />
                ) : (
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <LogIn className="h-5 w-5" />
                    <span>Iniciar Sesión</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </nav>

        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/torneo" element={<Torneo />} />
            <Route path="/reglamento" element={<Reglamento />} />
            <Route path="/buscar-pareja" element={<BuscarPareja />} />
            <Route path="/liga" element={<Liga />} />
            <Route path="/clases" element={<Clases />} />
            <Route path="/clases/calendario" element={<WeeklyCalendar />} />
            <Route path="/clases/admin-calendario" element={<AdminCalendar />} />
            <Route path="/clases/reservar" element={<SimpleCalendarBooking />} />
            <Route path="/clases/calendario-avanzado" element={<ClassCalendar />} />
            <Route path="/clases/mis-reservas" element={<MyBookings />} />
            <Route path="/clases/instructor" element={<InstructorDashboard />} />
            <Route path="/clases/admin" element={<AdminPanel />} />
          </Routes>
        </main>

        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      </div>
    </Router>
  );
}

function Clases() {
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });
  }, []);

  return (
    <div className="bg-white shadow rounded-lg p-8">
      <h2 className="text-3xl font-bold mb-6">Sistema de Clases</h2>
      
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-blue-800">
          <strong>Gestión Pública:</strong> La administración de clases, instructores y horarios es de acceso público. 
          Solo necesitas iniciar sesión para hacer reservas personales.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Link
          to="/clases/calendario"
          className="block p-6 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all bg-green-50 border-green-200"
        >
          <Calendar className="h-8 w-8 text-green-600 mb-3" />
          <h3 className="font-semibold text-lg mb-2">Calendario Interactivo</h3>
          <p className="text-gray-600 text-sm">
            Haz clic directamente en cualquier horario para reservar
          </p>
          <p className="text-xs text-green-600 mt-2 font-medium">✅ Sin login - Solo escribe tu nombre</p>
        </Link>

        <Link
          to="/clases/reservar"
          className="block p-6 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all"
        >
          <Calendar className="h-8 w-8 text-blue-600 mb-3" />
          <h3 className="font-semibold text-lg mb-2">Calendario de Reservas</h3>
          <p className="text-gray-600 text-sm">
            Vista de calendario semanal para reservas rápidas
          </p>
          <p className="text-xs text-green-600 mt-2 font-medium">✅ Acceso público - Solo escribe el nombre</p>
        </Link>

        <Link
          to="/clases/mis-reservas"
          className={`block p-6 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all ${
            !user ? 'opacity-75' : ''
          }`}
        >
          <Users className="h-8 w-8 text-green-600 mb-3" />
          <h3 className="font-semibold text-lg mb-2">Mis Reservas</h3>
          <p className="text-gray-600 text-sm">
            Ve y gestiona tus clases reservadas
          </p>
          {!user && <p className="text-xs text-yellow-600 mt-2">Requiere iniciar sesión</p>}
        </Link>

        <Link
          to="/clases/instructor"
          className={`block p-6 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all ${
            !user ? 'opacity-75' : ''
          }`}
        >
          <GraduationCap className="h-8 w-8 text-purple-600 mb-3" />
          <h3 className="font-semibold text-lg mb-2">Panel Instructor</h3>
          <p className="text-gray-600 text-sm">
            Gestiona tus clases y alumnos
          </p>
          {!user && <p className="text-xs text-yellow-600 mt-2">Requiere iniciar sesión</p>}
        </Link>

        <Link
          to="/clases/admin"
          className="block p-6 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all bg-green-50 border-green-200"
        >
          <Award className="h-8 w-8 text-red-600 mb-3" />
          <h3 className="font-semibold text-lg mb-2">Administración</h3>
          <p className="text-gray-600 text-sm">
            Gestiona instructores y horarios
          </p>
          <p className="text-xs text-green-600 mt-2 font-medium">✅ Acceso público disponible</p>
        </Link>

        <Link
          to="/clases/admin-calendario"
          className="block p-6 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all bg-yellow-50 border-yellow-200"
        >
          <Edit className="h-8 w-8 text-yellow-600 mb-3" />
          <h3 className="font-semibold text-lg mb-2">Admin Calendario</h3>
          <p className="text-gray-600 text-sm">
            Cambia niveles e instructores en tiempo real
          </p>
          <p className="text-xs text-yellow-600 mt-2 font-medium">⚙️ Panel de administrador</p>
        </Link>
      </div>

      <div className="mt-8 p-6 bg-blue-50 rounded-lg">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">Información de Clases</h3>
        <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded">
          <p className="text-green-800 text-sm">
            <strong>🎯 Nuevo Sistema:</strong> Calendario interactivo donde puedes hacer clic directamente en cualquier horario y escribir tu nombre para reservar. ¡Sin login requerido!
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div>
            <h4 className="font-medium mb-2">Horarios Disponibles:</h4>
            <ul className="space-y-1">
              <li>• Lunes a Viernes</li>
              <li>• Mañana: 7:00, 8:00, 9:00 AM</li>
              <li>• Tarde: 4:00, 5:00 PM</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium mb-2">Información Importante:</h4>
            <ul className="space-y-1">
              <li>• Máximo 4 alumnos por clase</li>
              <li>• 2 canchas disponibles</li>
              <li>• Cancelación mínimo 2 horas antes</li>
              <li>• <strong>Reserva directa sin login</strong></li>
              <li>• <strong>Gestión pública de clases</strong></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function HomePage() {
  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="relative h-96">
        <img
          src="https://images.pexels.com/photos/2352372/pexels-photo-2352372.jpeg"
          alt="Padel Courts"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-4xl font-bold mb-4">Bienvenidos a Padel Park</h1>
            <p className="text-xl">El mejor lugar para jugar pádel en San Luis</p>
          </div>
        </div>
      </div>
      <div className="p-8">
        <h2 className="text-2xl font-bold mb-4">Próximos Eventos</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="border rounded-lg p-4">
            <Trophy className="h-8 w-8 text-blue-600 mb-2" />
            <h3 className="font-semibold text-lg">4to Torneo Anual</h3>
            <p className="text-gray-600">¡Inscripciones abiertas!</p>
          </div>
          <div className="border rounded-lg p-4">
            <Calendar className="h-8 w-8 text-blue-600 mb-2" />
            <h3 className="font-semibold text-lg">Clases Grupales</h3>
            <p className="text-gray-600">Todos los niveles</p>
          </div>
          <div className="border rounded-lg p-4">
            <Award className="h-8 w-8 text-blue-600 mb-2" />
            <h3 className="font-semibold text-lg">Liga Interna</h3>
            <p className="text-gray-600">¡Participa y gana!</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Torneo() {
  return (
    <div className="bg-white shadow rounded-lg p-8">
      <h2 className="text-3xl font-bold mb-6">4to Torneo Anual Padel Park</h2>
      
      <div className="mb-8">
        <img 
          src="/Flyer-2025_padel-park_actualizado.jpg"
          alt="4to Torneo Anual Padel Park"
          className="w-full max-w-3xl mx-auto rounded-lg shadow-lg"
        />
      </div>

      <div className="space-y-8">
        <div>
          <h3 className="text-2xl font-semibold mb-4">Fechas del Torneo</h3>
          <p className="text-lg text-gray-700">Del 15 de Junio al 28 de Junio 2025</p>
          <p className="text-gray-600 mt-2">Fecha límite de inscripción: 11 de Junio</p>
        </div>

        <div>
          <h3 className="text-2xl font-semibold mb-4">Categorías</h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-xl font-semibold mb-2">Varonil</h4>
              <p className="text-gray-600">1ra, 2da, 3ra, 4ta, 5ta, 6ta, 7ma</p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-xl font-semibold mb-2">Femenil</h4>
              <p className="text-gray-600">3ra, 4ta, 5ta, 6ta</p>
              <p className="text-sm text-gray-500 mt-2">*Mínimo 12 parejas para abrir categoría</p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-xl font-semibold mb-2">Mixta</h4>
              <p className="text-gray-600">C, D</p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-semibold mb-4">Premios</h3>
          <div className="space-y-3">
            <p className="text-xl">1er Lugar: <span className="font-bold text-green-600">$20,000.00</span></p>
            <p className="text-xl">2do Lugar: <span className="font-bold text-green-600">$16,000.00</span></p>
            <p className="text-lg">3era a 7ma: 2 Palas ACA LTD</p>
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-semibold mb-4">Inscripción</h3>
          <div className="space-y-3">
            <p className="text-lg">Costo por persona: <span className="font-bold">$950.00</span></p>
            <p className="text-lg">Reserva tu lugar con: <span className="font-bold">$300.00</span></p>
            <div className="mt-4">
              <p className="font-semibold">Informes:</p>
              <p>WhatsApp: 444 204 4524</p>
              <p>WhatsApp: 444 236 8253</p>
            </div>
          </div>
        </div>

        <div>
          <p className="text-blue-600 font-semibold">¡Playera oficial para las primeras 150 inscripciones pagadas!</p>
        </div>
      </div>
    </div>
  );
}

function Reglamento() {
  return (
    <div className="bg-white shadow rounded-lg p-8">
      <h2 className="text-3xl font-bold mb-6">REGLAMENTO Y CLAUSULAS DEL 4TO TORNEO ANUAL DE PADEL PARK</h2>
      
      <div className="prose max-w-none space-y-8">
        <div>
          <h3 className="text-2xl font-semibold mb-4">Duración del Torneo y Acomodo de Juegos</h3>
          <p className="text-lg mb-4">Domingo 15 de Junio a Sábado 28 de junio.</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><span className="font-semibold">Fase de grupos:</span> 15 de Junio a 22 de Junio (Cada pareja juega 2-3 partidos, dependiendo si su grupo es de 3 o 4 parejas)</li>
            <li><span className="font-semibold">Fase de Eliminación directa:</span> 23 de Junio al 28 de Junio. (Se juegan dieciseisavos, Octavos, Cuartos, Semifinales y Final, dependiendo lo que cada categoría requiera.)</li>
          </ul>
        </div>

        <div className="space-y-6">
          <div>
            <h4 className="text-xl font-semibold mb-3">1. Horarios de Partidos</h4>
            <p className="mb-2">Los partidos pueden ser agendados en cualquier de los siguientes días u horarios:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><span className="font-semibold">Lunes a viernes:</span> 6:30am, 7:00am, 8:15am o 9:00am, 5:00pm, 6:15pm, 730pm, 9:00pm</li>
              <li><span className="font-semibold">Sábados y domingos:</span> 8:00am, 9:15am, 10:30am, 11:45am, 1:00pm, 2:15pm, 3:30pm, 4:45pm, 6:00pm</li>
            </ul>
            <p className="mt-2 text-gray-700">Es responsabilidad de los participantes tener amplia disponibilidad para jugar en cualquiera de estos horarios, o enviar su disponibilidad vía WhatsApp antes del 10 de Junio del 2025.</p>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-3">2. Cambios de Partido</h4>
            <div className="text-gray-700">
              Es responsabilidad de los participantes, solicitar por escrito a la otra pareja, cualquier cambio de partido. Si la otra pareja acepta, el juego se deberá jugar en otro club (El costo de la cancha deberá ser cubierta por los jugadores, según se negocie) y el resultado lo deberán pasar vía whatsapp a los organizadores dentro del plazo establecido, cualquier resultado que no se pase en este plazo será considerado como partido perdido para ambas parejas, sin juegos a favor ni en contra.
            </div>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-3">3. Límite de Parejas y Acomodo</h4>
            <div className="text-gray-700">
              El máximo de parejas por categoría es de 32 parejas, sin embargo hay categorías que pueden quedar con numero impares, por lo que las estepas de eliminación directa, pueden ser irregulares, por lo mismo el acomodo de los play offs puede permitirse elegir a alguna pareja al asar para asignar los juegos, o acomodar a un primer lugar de grupo contra otro primer lugar de grupo, de ser necesario o si el acomodo lo requiere. El staff organizador no se hace responsable de cualquier queja que pueda surgir, de los acomodos posteriores en los playoffs, ni en la fase de grupos.
            </div>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-3">4. Liquidación del Torneo</h4>
            <div className="text-gray-700">
              El torneo debe quedar liquidado a mas tardar el 11 de junio, en caso de que no este pagado en su totalidad y por parte de los 2 jugadores, la pareja no podrá ser enrolada, y se dará de baja automáticamente, sin excepción alguna.
            </div>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-3">5. Categorías y Bajas</h4>
            <div className="text-gray-700 space-y-4">
              <p>
                Cualquier pareja que no pertenezca a la categoría, será dada de baja sin derecho a reembolso (A menos que sea dada de baja antes del 11 de junio). El staff organizador será el encargado de tomar la decisión lo mas objetivo e imparcial posible. Esta decisión será inapelable.
              </p>
              <p>
                El criterio para saber si una pareja pertenece o no a la categoria, es decidido por el staff organizador y no podra ser influenciado por ningun participante del torneo.
              </p>
              <p>
                Padel Park permite el ingreso a jugadores en varias categorias, y una pareja podra ingresar a una categoria inferior, siempre y cuando no haya ganado o llegado a semifinales (*La pareja completa) de algun torneo en Padel Park, Marietta, Advantage, Rou, Madness, Club deportivo potosino, La loma.
              </p>
              <p>
                En caso de que solo un integrante de la pareja haya hecho lo anterior, se considerara al otro integrante para definir el nivel adecuado de la misma. Si se considera que en conjunto esta pareja tiene un nivel correcto para pertenecer a la categoria, se podra dar el visto bueno.
              </p>
            </div>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-3">6. Arbitraje</h4>
            <div className="text-gray-700">
              El torneo no cuenta con árbitros oficiales, toda decisión en cuanto al desarollo del juego deberá tomarse por los participantes y deberán llegar a un acuerdo, en caso de que un partido termine por no ponerse de acuerdo en una decisión, o alguna discusión/pelea por parte de los participantes, el juego será considerado como partido perdido para ambas parejas, sin juegos a favor ni en contra.
            </div>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-3">7. Responsabilidad del Staff</h4>
            <div className="text-gray-700">
              Padel Park y el staff organizador no se hacen responsables de cualquier queja, inconformidad o situación que pueda surgir durante el torneo, debido a la alta carga de partidos y alta demanda de solicitudes por parte de los participantes, es común que pueda surgir algún error en cuanto a algún acomodo de partidos o cualquier situación relacionada al torneo. Por lo que ningún miembro del staff organizador se hace responsable de cualquiera situación deportiva o general que pueda surgir.
            </div>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-3">8. Situaciones Extraordinarias</h4>
            <div className="text-gray-700">
              En caso de alguna situación extraoficial en el cual el torneo no pueda concluirse en tiempo y forma, y la fecha de finalización pueda verse comprometida, Padel Park y el staff organizador no se hacen responsables, y se tomaran las medidas necesarias para poder finalizar el torneo.
            </div>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-3">9. Política de Reembolsos</h4>
            <div className="text-gray-700">
              Padel Park y el staff organizador no se hacen responsables de dar reembolsos a los jugadores, por ninguna situación, incluyendo las siguientes: cambio en la decisión de compra, lesión, eventos personales, quejas o inconformidades relacionadas al desarrollo u organización del torneo, o cualquier causa fuera de las fechas establecidas sin autorización previa.
            </div>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-3">10. Normas de Conducta</h4>
            <div className="text-gray-700">
              Padel park y el staff organizador podrá dar de baja del torneo o expulsar de las instalaciones, a cualquier persona que no cumpla con las normas del reglamento interno del torneo y del club, ademas de los requisitos y normas impuestas por la autoridad, Comercio y las dependencias como Ecología, Cofepris, Protección Civil etc etc, llámese esto a cualquier alteración al orden publico, ruido excesivo, conducta violenta o intimidante, o uso de las instalaciones fuera del horario permitido.
            </div>
          </div>

          <div>
            <h4 className="text-xl font-semibold mb-3">11. Aceptación de Términos</h4>
            <div className="text-gray-700">
              Todo participante del torneo, acepta estos términos y condiciones, al momento de solicitar la inscripción e ingreso al mismo, es responsabilidad del usuario el leer, este reglamento en su totalidad, Padel Park y los organizadores no se hacen responsables, si alguna persona no lee el documento en su totalidad o no esta de acuerdo con alguna de las cláusulas aquí propuestas, o en el reglamento interno del club.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BuscarPareja() {
  const [user, setUser] = useState<any>(null);
  const [formData, setFormData] = useState({
    nombre: '',
    categoria: '',
    posicion: '',
    whatsapp: '',
    comentarios: ''
  });

  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });
  }, []);

  useEffect(() => {
    fetchPartners();

    const subscription = supabase
      .channel('partners_channel')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'partners' },
        (payload) => {
          fetchPartners();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchPartners = async () => {
    try {
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .order('fecha_publicacion', { ascending: false });

      if (error) throw error;
      setPartners(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      alert('Debes iniciar sesión para publicar un anuncio');
      return;
    }
    
    try {
      const { error } = await supabase
        .from('partners')
        .insert([{
          ...formData,
          torneo: '4to Torneo Anual',
          user_id: user.id
        }]);
      
      if (error) throw error;
      
      setFormData({
        nombre: '',
        categoria: '',
        posicion: '',
        whatsapp: '',
        comentarios: ''
      });
      
      alert('¡Anuncio publicado exitosamente!');
    } catch (error) {
      alert('Error al publicar el anuncio: ' + error.message);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="bg-white shadow rounded-lg p-8">
      <h2 className="text-3xl font-bold mb-6">Busco Pareja</h2>
      
      <div className="grid gap-8 md:grid-cols-2">
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-xl font-semibold mb-4">Publicar Anuncio</h3>
          
          {!user && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <p className="text-yellow-800 text-sm">
                Debes iniciar sesión para publicar un anuncio
              </p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-1">
                Nombre
              </label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="categoria" className="block text-sm font-medium text-gray-700 mb-1">
                Categoría
              </label>
              <select
                id="categoria"
                name="categoria"
                value={formData.categoria}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Selecciona una categoría</option>
                <option value="1ra">1ra Fuerza</option>
                <option value="2da">2da Fuerza</option>
                <option value="3ra">3ra Fuerza</option>
                <option value="4ta">4ta Fuerza</option>
                <option value="5ta">5ta Fuerza</option>
                <option value="6ta">6ta Fuerza</option>
                <option value="7ma">7ma Fuerza</option>
              </select>
            </div>

            <div>
              <label htmlFor="posicion" className="block text-sm font-medium text-gray-700 mb-1">
                Posición
              </label>
              <select
                id="posicion"
                name="posicion"
                value={formData.posicion}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Selecciona una posición</option>
                <option value="Drive">Drive</option>
                <option value="Reves">Revés</option>
                <option value="Ambas">Ambas</option>
              </select>
            </div>

            <div>
              <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp
              </label>
              <input
                type="tel"
                id="whatsapp"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleChange}
                required
                placeholder="444 123 4567"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="comentarios" className="block text-sm font-medium text-gray-700 mb-1">
                Comentarios
              </label>
              <textarea
                id="comentarios"
                name="comentarios"
                value={formData.comentarios}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Información adicional, horarios disponibles, etc."
              ></textarea>
            </div>

            <button
              type="submit"
              disabled={!user}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Publicar Anuncio
            </button>
          </form>
        </div>

        <div>
          <h3 className="text-xl font-semibold mb-4">Anuncios Recientes</h3>
          {loading ? (
            <div className="text-center py-4">Cargando anuncios...</div>
          ) : error ? (
            <div className="text-red-600 text-center py-4">Error: {error}</div>
          ) : partners.length === 0 ? (
            <div className="text-center py-4 text-gray-600">No hay anuncios publicados</div>
          ) : (
            <div className="space-y-4">
              {partners.map((partner) => (
                <div key={partner.id} className="border rounded-lg p-4 bg-white">
                  <h4 className="font-semibold">{partner.nombre}</h4>
                  <p className="text-sm text-gray-600">Categoría: {partner.categoria}</p>
                  <p className="text-sm text-gray-600">Posición: {partner.posicion}</p>
                  <p className="text-sm text-gray-600">WhatsApp: {partner.whatsapp}</p>
                  {partner.comentarios && (
                    <p className="text-sm text-gray-700 mt-2">{partner.comentarios}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    {new Date(partner.fecha_publicacion).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Liga() {
  const [activeTab, setActiveTab] = useState('convocatoria');

  return (
    <div className="bg-white shadow rounded-lg p-8">
      <h1 className="text-3xl font-bold mb-6">Liga Padel Park</h1>
      
      <div className="flex space-x-4 mb-8">
        <button
          onClick={() => setActiveTab('convocatoria')}
          className={`flex items-center px-6 py-3 rounded-lg ${
            activeTab === 'convocatoria'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <FileText className="h-5 w-5 mr-2" />
          Convocatoria
        </button>
        <button
          onClick={() => setActiveTab('posiciones')}
          className={`flex items-center px-6 py-3 rounded-lg ${
            activeTab === 'posiciones'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Table className="h-5 w-5 mr-2" />
          Tabla de Posiciones
        </button>
        <button
          onClick={() => setActiveTab('premios')}
          className={`flex items-center px-6 py-3 rounded-lg ${
            activeTab === 'premios'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Trophy className="h-5 w-5 mr-2" />
          Premios
        </button>
      </div>

      {activeTab === 'convocatoria' && (
        <div>
          <h2 className="text-2xl font-bold mb-4">Convocatoria Liga Padel Park 2025</h2>
          <img
            src="https://images.pexels.com/photos/2352372/pexels-photo-2352372.jpeg"
            alt="Padel Courts"
            className="w-full h-64 object-cover rounded-lg mb-6"
          />
          <div className="space-y-6">
            <section>
              <h3 className="text-xl font-semibold mb-3">Fechas Importantes</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Inicio de inscripciones: 01 Julio 2025</li>
                <li>Cierre de inscripciones: 10 Julio 2025</li>
                <li>Inicio de la liga: 18 de Julio 2025</li>
                <li>Final de temporada: 29 de Agosto 2025</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">Categorías</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Segunda Fuerza</li>
                <li>Tercera Fuerza</li>
                <li>Cuarta Fuerza</li>
                <li>Quinta Fuerza</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold mb-3">Requisitos</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Registro completo de la pareja</li>
                <li>Pago de inscripción</li>
                <li>Disponibilidad para jugar en horarios establecidos</li>
              </ul>
            </section>
          </div>
        </div>
      )}

      {activeTab === 'posiciones' && (
        <div>
          <h2 className="text-2xl font-bold mb-6">Tabla de Posiciones</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Posición</th>
                  <th className="px-6 py-3  text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Equipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PJ</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PG</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PP</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Puntos</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">1</td>
                  <td className="px-6 py-4 whitespace-nowrap">Equipo A</td>
                  <td className="px-6 py-4 whitespace-nowrap">5</td>
                  <td className="px-6 py-4 whitespace-nowrap">4</td>
                  <td className="px-6 py-4 whitespace-nowrap">1</td>
                  <td className="px-6 py-4 whitespace-nowrap">12</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">2</td>
                  <td className="px-6 py-4 whitespace-nowrap">Equipo B</td>
                  <td className="px-6 py-4 whitespace-nowrap">5</td>
                  <td className="px-6 py-4 whitespace-nowrap">3</td>
                  <td className="px-6 py-4 whitespace-nowrap">2</td>
                  <td className="px-6 py-4 whitespace-nowrap">9</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'premios' && (
        <div>
          <h2 className="text-2xl font-bold mb-6">Premios de la Liga</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-yellow-100 to-yellow-200 p-6 rounded-lg">
              <Trophy className="h-10 w-10 text-yellow-600 mb-3" />
              <h3 className="text-lg font-bold mb-2">1er Lugar</h3>
              <p className="text-gray-700">PENDIENTE</p>
            </div>
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 p-6 rounded-lg">
              <Award className="h-10 w-10 text-gray-600 mb-3" />
              <h3 className="text-lg font-bold mb-2">2do Lugar</h3>
              <p className="text-gray-700">PENDIENTE</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;